import { useEffect, useState } from "react";
import { X, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const KEY = "vault.whatsnew.v2.dismissed";

const ITEMS = [
    { title: "VAULT Brief",           body: "Know exactly what needs your attention today, this week, this month." },
    { title: "New Dashboard",         body: "Financial Position + Money Flow at a glance, in the new MIDNIGHT LUXE design." },
    { title: "Enhanced Analytics",    body: "Cash-flow Sankey with semantic categories and premium interactions." },
    { title: "VAULT Together",        body: "Mine · Partner · Joint · Together — shared household finances without giving up privacy." },
    { title: "Improved Documents",    body: "Documents you can actually find, categorised the way finance works." },
];

/**
 * WELCOME TO THE NEW VAULT — one-time post-login modal.
 * Only appears when `ui_redesign_v2` is enabled for the user AND
 * they haven't dismissed it. Dismissal is remembered in localStorage.
 */
export default function WhatsNewModal({ enabled = false }) {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!enabled) return;
        try {
            if (localStorage.getItem(KEY) === "1") return;
            const t = setTimeout(() => setOpen(true), 800);
            return () => clearTimeout(t);
        } catch { /* silent */ }
    }, [enabled]);

    function dismiss() {
        setOpen(false);
        try { localStorage.setItem(KEY, "1"); } catch { /* silent */ }
    }

    if (!open) return null;
    return (
        <div
            className="v2-scope fixed inset-0 z-[80] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            data-testid="v2-whatsnew-modal"
        >
            <div
                className="absolute inset-0"
                style={{ background: "rgba(10, 20, 36, 0.68)", backdropFilter: "blur(6px)" }}
                onClick={dismiss}
            />
            <div
                className="v2-hero relative"
                style={{ maxWidth: 560, width: "100%", padding: "36px 34px 32px", animation: "v2-modal-in 380ms cubic-bezier(0.16,1,0.3,1) both" }}
            >
                <button
                    type="button"
                    onClick={dismiss}
                    aria-label="Close"
                    className="absolute top-3 right-3 p-2 rounded-full"
                    style={{ color: "var(--v2-ink-inv-soft)" }}
                    data-testid="v2-whatsnew-close"
                >
                    <X size={16} />
                </button>

                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <Sparkles size={14} style={{ color: "var(--v2-gold-soft)" }} strokeWidth={2.2}/>
                    <span className="v2-eyebrow" style={{ color: "var(--v2-gold-soft)" }}>
                        Welcome to the new VAULT
                    </span>
                </div>
                <h2 className="v2-greeting__hello" style={{ fontSize: 34, marginBottom: 10 }}>
                    A fresher experience.<br/>Smarter insights.
                </h2>
                <p className="v2-greeting__lede" style={{ maxWidth: 460, marginBottom: 22 }}>
                    We&rsquo;ve rebuilt the VAULT experience around the moments that matter — what needs
                    your attention, where your money went, and how it&rsquo;s trending.
                </p>

                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px", display: "grid", gap: 10 }}>
                    {ITEMS.map((it) => (
                        <li key={it.title} className="v2-brief-row" style={{ padding: "10px 0", gap: 14 }}>
                            <span className="v2-brief-row__count" style={{ fontSize: 22, minWidth: 22, color: "var(--v2-gold-soft)" }}>›</span>
                            <span style={{ flex: 1 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--v2-ink-inv)" }}>{it.title}</div>
                                <div className="v2-brief-row__label" style={{ fontSize: 12.5 }}>{it.body}</div>
                            </span>
                        </li>
                    ))}
                </ul>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <Link
                        to="/dashboard"
                        onClick={dismiss}
                        className="v2-btn v2-btn--gold"
                        data-testid="v2-whatsnew-explore"
                    >
                        Explore what&rsquo;s new <ArrowRight size={13} />
                    </Link>
                    <button
                        type="button"
                        onClick={dismiss}
                        className="v2-btn v2-btn--ghost"
                        data-testid="v2-whatsnew-dismiss"
                    >
                        Not now
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes v2-modal-in {
                    from { opacity: 0; transform: translateY(14px) scale(0.98); }
                    to   { opacity: 1; transform: translateY(0)  scale(1); }
                }
            `}</style>
        </div>
    );
}
